import { v4 as uuid } from 'uuid';

import { SlideMetadata } from './types';
import Logger from '../../utils/logger';

const PRESENTATION_ID = 'ACADLY_PRESENTATION_ID';
const HAS_METADATA_ON_BACKEND = 'HAS_ACADLY_METADATA_ON_BACKEND';
const ADDIN_NAME = 'ACADLY_ADDIN';
const ADDIN_OFFSET_SIZE = 16;
const DEFAULT_ADDIN_WIDTH = 960 - ADDIN_OFFSET_SIZE * 2;
const DEFAULT_ADDIN_HEIGHT = 540 - ADDIN_OFFSET_SIZE * 2;

export function getSlideMetadata(): Promise<SlideMetadata | undefined> {
  return new Promise((resolve, reject) => {
    Office.context.document.getSelectedDataAsync(Office.CoercionType.SlideRange, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        Logger.error(asyncResult.error);
        reject(asyncResult.error);
      } else if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
        const jsonString = JSON.stringify(asyncResult.value);
        const slideRange = JSON.parse(jsonString);

        if (slideRange.slides && slideRange.slides.length > 0) {
          resolve(slideRange.slides[0]);
        } else {
          resolve(undefined);
        }
      }
    });
  });
}

export async function getIsPresenting() {
  return new Promise<boolean>((resolve, reject) => {
    Office.context.document.getActiveViewAsync(function (result) {
      if (result.status === Office.AsyncResultStatus.Failed) {
        return reject(result.error);
      }

      if (result.value === 'edit') {
        return resolve(false);
      }

      resolve(true);
    });
  });
}
export async function onActiveViewChanged({
  onEditMode,
  onPresentationMode,
}: {
  onEditMode?: () => void;
  onPresentationMode?: () => void;
}) {
  Office.context.document.getActiveViewAsync(function (result) {
    if (result.status === Office.AsyncResultStatus.Failed) {
      return;
    }

    if (result.value === 'edit') {
      onEditMode?.();
      return;
    }

    onPresentationMode?.();
  });
}

export async function getActiveSlideId() {
  return new Promise<string | undefined>((resolve) => {
    PowerPoint.run(async function (context) {
      try {
        await context.sync();

        const data = await getSlideMetadata();
        if (!data) {
          resolve(undefined);
          return;
        }

        const selectedSlide = context.presentation.slides.getItemAt(data.index - 1);

        selectedSlide.load({ id: true });
        await context.sync();

        resolve(selectedSlide.id);
      } catch (error) {
        Logger.error(error);
      }
    });
  });
}

export async function getOrCreatePresentationId() {
  return new Promise<{ presentationId: string; isNewPresentation: boolean }>((resolve, reject) => {
    PowerPoint.run(async function (context) {
      const presentation = context.presentation;
      presentation.load('tags');
      await context.sync();
      const presentationTags = presentation.tags;
      try {
        // getItem throws an error when key does not exist
        // keep keys in captial as PowerPoint automatically capitalizes keys when adding new tag but does not automatically capitalize keys when getting tags
        const presentationId = presentationTags.getItem(PRESENTATION_ID);
        presentationId.load('value');
        await context.sync();
        return resolve({ presentationId: presentationId.value, isNewPresentation: false });
      } catch (error) {
        const newPresentationId = uuid();
        presentationTags.add(PRESENTATION_ID, newPresentationId);
        await context.sync();
        resolve({ presentationId: newPresentationId, isNewPresentation: true });
      }
    });
  });
}

export async function getHasMetadataOnBackend() {
  return new Promise<boolean>((resolve, reject) => {
    PowerPoint.run(async function (context) {
      const presentation = context.presentation;
      presentation.load('tags');
      await context.sync();
      const presentationTags = presentation.tags;
      try {
        // getItem throws an error when key does not exist
        // keep keys in captial as PowerPoint automatically capitalizes keys when adding new tag but does not automatically capitalize keys when getting tags
        const hasMetadata = presentationTags.getItem(HAS_METADATA_ON_BACKEND);
        hasMetadata.load('value');
        await context.sync();
        return resolve(hasMetadata.value === '1');
      } catch (error) {
        Logger.error(error);
        resolve(true);
      }
    });
  });
}

export async function setHasMetadataOnBackend(newValue: boolean) {
  const value = newValue ? '1' : '0';
  return new Promise<boolean>((resolve, reject) => {
    PowerPoint.run(async function (context) {
      const presentation = context.presentation;
      presentation.load('tags');
      await context.sync();
      const presentationTags = presentation.tags;
      try {
        // getItem throws an error when key does not exist
        // keep keys in captial as PowerPoint automatically capitalizes keys when adding new tag but does not automatically capitalize keys when getting tags
        const hasMetadata = presentationTags.getItem(HAS_METADATA_ON_BACKEND);
        hasMetadata.load('value');
        await context.sync();
        if (hasMetadata.value === value) return resolve(newValue);
        hasMetadata.value = value;
        await context.sync();
        return resolve(newValue);
      } catch (error) {
        Logger.error(error);
        presentationTags.add(HAS_METADATA_ON_BACKEND, value);
        await context.sync();
        return resolve(newValue);
      }
    });
  });
}

export async function resizeAddin() {
  return new Promise<void>((resolve, reject) => {
    PowerPoint.run(async function (context) {
      const presentation = context.presentation;

      const activeSlideMetadata = await getSlideMetadata();
      if (!activeSlideMetadata) return;

      const slide = presentation.slides.getItemAt(activeSlideMetadata.index - 1);

      const shapes = slide.shapes;
      shapes.load();

      await context.sync();

      for (let i = 0; i < shapes.items.length; i++) {
        const shape = shapes.items[i];
        if (shape.name.includes('Add-in') || shape.name.includes('OfficeApp')) {
          const addinTags = shape.tags;
          try {
            const addinName = addinTags.getItem(ADDIN_NAME);
            addinName.load('value');
            await context.sync();
          } catch (err) {
            Logger.error(err);
            addinTags.add(ADDIN_NAME, ADDIN_NAME);
            await context.sync();
            shape.left = ADDIN_OFFSET_SIZE;
            shape.top = ADDIN_OFFSET_SIZE;
            shape.width = DEFAULT_ADDIN_WIDTH;
            shape.height = DEFAULT_ADDIN_HEIGHT;
          } finally {
            await context.sync();
            resolve();
            break;
          }
        }
      }
    });
  });
}

/**
 *
 * @param activeSlideId Enter slideId in witch you want to change title and subTitle
 * @param title Enter title if you want multi line then provide string like 'title \r secondline'. DO NOT USE \n as it raises many bugs in PowerPoint code.
 * @param subTitle Enter subTitle if you want multi line then provide string like 'subTitle \r secondline'.  DO NOT USE \n for consistency with title.
 */
export function setTitleAndSubtitle(activeSlideId: string, title: string, subTitle: string) {
  PowerPoint.run(async function (context) {
    const slides = context.presentation.slides;
    slides.load('items'); // Load all slides
    await context.sync();

    const activeSlide = slides.items.find((s) => s.id == activeSlideId);
    if (!activeSlide) return;

    const shapes = activeSlide.shapes;
    shapes.load();
    await context.sync();

    for (let i = 0; i < shapes.items.length; i++) {
      const shape = shapes.items[i];

      if (shape.name.includes('Title')) {
        shape.textFrame.deleteText();
        shape.textFrame.textRange.text = title;
      }

      if (shape.name.includes('Subtitle')) {
        shape.textFrame.deleteText();
        shape.textFrame.textRange.text = subTitle;
      }
    }

    await context.sync();
  });
}

export function getIsSlideTitleSet(activeSlideId: string) {
  return new Promise<boolean>(async (resolve, reject) => {
    await PowerPoint.run(async function (context) {
      const slides = context.presentation.slides;
      slides.load('items'); // Load all slides
      await context.sync();

      const activeSlide = slides.items.find((s) => s.id == activeSlideId);
      if (!activeSlide) return;

      const shapes = activeSlide.shapes;
      shapes.load();
      await context.sync();

      for (let i = 0; i < shapes.items.length; i++) {
        const shape = shapes.items[i];

        if (shape.name.includes('Title')) {
          shape.textFrame.textRange.load('text');
          await context.sync();
          // we assume that if title of the slide starts with ACADLY, we have set title already
          const isTitleSet = shape.textFrame.textRange.text.startsWith('ACADLY');
          return resolve(isTitleSet);
        }
      }

      await context.sync();
    });

    return resolve(false);
  });
}

export function onSlideSelectionChanged(callback: () => any) {
  Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, callback);
}
